import { OperationalStatusEnum } from 'fleet-data-model';
import type { Power, AssetListItem } from '@fleet-bffs/api-contract';
import AssetType from '~/constants/asset-type-constants';
import ShortGenericNameConstants from '~/constants/short-generic-name-constants';
import OtherMachineStatusConstants from '~/constants/other-machine-status-constants';
import type RoboticMowerSetting from '~/ducks/robotic-mower-command/robotic-mower-setting';
import {
  InventoryStatusFields,
  BorderMowingType,
  GeneralAccessoryAsset,
  LocationFields,
} from '~/data/Asset';
import type { RelationshipTag } from '~/ducks/asset-tag-relationships/types';
import type { IconName } from '~uikit/Icon/icon-names';
import type { RoboticMowerListItem, NotRoboticMowerListItem } from './AssetListItem';

export const hasAvailableSetting = (asset: AssetListItem, setting: RoboticMowerSetting) =>
  isRoboticMower(asset) && asset.roboticMower.availableSettings.includes(setting);

export const getName = (asset: AssetListItem): string => {
  return asset.assetDetails.name;
};

export const getLocation = (asset: AssetListItem): LocationFields | null => {
  // If machineData present, only machineData.location consumption is allowed
  return asset?.machineData ? asset?.machineData?.location : asset?.location;
};

export const getPower = (asset: AssetListItem): Power | null => {
  return asset?.machineData?.power;
};

export const getStatusTimestamp = (asset: RoboticMowerListItem): number => {
  return asset.roboticMower.status.timestamp;
};

export const isConnectivityModule = (asset: AssetListItem): boolean => {
  return asset.assetDetails?.shortGenericName === ShortGenericNameConstants.ConnectivityModule;
};

export const getIsHcm500 = (asset: AssetListItem): asset is NotRoboticMowerListItem => {
  if (asset.assetDetails?.shortGenericName !== undefined) {
    // Detection based on data from new architecture
    return asset.assetDetails.shortGenericName === ShortGenericNameConstants.Gateway;
  }
  return isGeneralAccessory(asset) && asset.assetDetails.model === 'HCM 500';
};

export const isMowerSettingsAvailable = (
  asset: AssetListItem,
  generalAllowed: boolean,
  securityAllowed: boolean,
  controlAllowed: boolean,
  mowerCentricSettingsAvailable: boolean,
  securitySettingsAvailable: boolean,
  hasInstallationSettings: boolean
): boolean => {
  const isRoboticMower = asset?.roboticMower;
  const hasMowerCentricSettings = isRoboticMower && mowerCentricSettingsAvailable && generalAllowed;
  const hasSecuritySettings = securityAllowed && securitySettingsAvailable;

  return Boolean(
    isRoboticMower && (hasMowerCentricSettings || hasInstallationSettings || hasSecuritySettings)
  );
};

export const isRoboticMower = (asset: AssetListItem): asset is RoboticMowerListItem => {
  if (asset.assetDetails?.shortGenericName) {
    // Detection based on data from new architecture
    return asset.assetDetails?.shortGenericName === ShortGenericNameConstants.RoboticLawnMower;
  }
  return asset.assetDetails.assetType === AssetType.ROBOTIC_MOWER;
};

export const isRoboticMowerInErrorState = (asset: AssetListItem): boolean => {
  if (asset.status?.operationalStatus?.status !== undefined) {
    const fineStatuses = [OperationalStatusEnum.OK, OperationalStatusEnum.WARNING];
    return !fineStatuses.includes(asset.status.operationalStatus?.status);
  }
  return false;
};

export const isRoboticMowerDisconnected = (asset: AssetListItem): boolean => {
  return !asset?.machineData?.connectivity?.connected;
};

export const isBattery = (asset: AssetListItem) => {
  if (asset.assetDetails?.shortGenericName) {
    // Detection based on data from new architecture
    return asset.assetDetails?.shortGenericName === ShortGenericNameConstants.Battery;
  }
  return asset.assetDetails.assetType === AssetType.BATTERY;
};

export const isGeneralAccessory = (asset: AssetListItem): asset is GeneralAccessoryAsset => {
  return asset.assetDetails.assetType === AssetType.GENERAL_ACCESSORIES;
};

export const getLocationName = ({ location }: AssetListItem): string | undefined => {
  if (!location) {
    return;
  }

  if (location.locationName) {
    return location.locationName;
  }

  return `${location.latitude}, ${location.longitude}`;
};

export const getInventoryStatus = (
  asset: AssetListItem
): NotRoboticMowerListItem['inventoryStatus'] | RoboticMowerListItem['roboticMower']['status'] => {
  return isRoboticMower(asset) ? asset.roboticMower.status : asset.inventoryStatus;
};

export const canShowInMap = (asset: AssetListItem): boolean => {
  if (asset?.machineData) {
    return (
      asset?.machineData?.location?.latitude !== undefined &&
      asset?.machineData?.location?.longitude !== undefined
    );
  }

  const hasLocation = Boolean(getLocation(asset));

  if (isRoboticMower(asset) || isGeneralAccessory(asset)) {
    return hasLocation;
  }

  const inventoryStatus = getInventoryStatus(asset) as InventoryStatusFields | null;

  return Boolean(
    hasLocation &&
      inventoryStatus &&
      ![
        OtherMachineStatusConstants.NOT_CONNECTED,
        OtherMachineStatusConstants.NO_MACHINE_SENSOR,
        OtherMachineStatusConstants.NON_ACTIVATED,
        OtherMachineStatusConstants.NEVER_SEEN,
      ].includes(inventoryStatus.status)
  );
};

export const getTags = (asset: AssetListItem): RelationshipTag[] => {
  return asset.tags.reduce<RelationshipTag[]>(
    (acc, tagCategory) => acc.concat(tagCategory.tags),
    []
  );
};

export const isConnected = (asset: AssetListItem): boolean =>
  Boolean(
    asset.inventoryStatus &&
      ![
        OtherMachineStatusConstants.NOT_CONNECTED,
        OtherMachineStatusConstants.NO_MACHINE_SENSOR,
      ].includes(asset.inventoryStatus.status)
  );

export const getBorderMowingTypeIcon = (
  borderMovingType?: BorderMowingType,
  defaultValue: IconName = 'border-mowing-variable'
): IconName => {
  switch (borderMovingType) {
    case BorderMowingType.FIXED:
      return 'border-mowing-fixed';

    case BorderMowingType.VARIABLE:
      return 'border-mowing-variable';

    default:
      return defaultValue;
  }
};
